/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from 'react';
import { NavLink, Link } from "react-router-dom";
import MuiLink from "@mui/material/Link";
// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';


// Material Dashboard 2 React components
import MDBox from "dashboard/components/MDBox";
import MDTypography from "dashboard/components/MDTypography";
// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "dashboard/examples/Sidenav/styles/sidenavCollapse";

// Material Dashboard 2 React context
import { useMaterialUIController } from "context";

function SidenavCollapse({ icon, name, active, items, isTopbar = false ,collapseName, ...rest }) {
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const [open, setOpen] = useState(false);
 
  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <>
     {items && items.length === 0  ? 
        (<ListItem component="li">
          {!isTopbar ? (<MDBox
            {...rest}
            sx={(theme) => collapseItem(theme, {
              active,
              transparentSidenav,
              whiteSidenav,
              darkMode,
              sidenavColor
            })}
          >
            <ListItemIcon
              sx={(theme) => collapseIconBox(theme, { transparentSidenav, whiteSidenav, darkMode, active })}
            >
              {typeof icon === "string" ? (
                <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
              ) : (
                icon
              )}
            </ListItemIcon>
            <ListItemText
              primary={name}
              sx={(theme) => collapseText(theme, {
                miniSidenav,
                transparentSidenav,
                whiteSidenav,
                active
              })} />
          </MDBox>)
          :  ( 
            <MDTypography
                  key={name}
                  minWidth="11.25rem"
                  display="block"
                  variant="button"
                  color="text"
                  textTransform="capitalize"
                  fontWeight="regular"
                  py={0.625}
                  px={2}
                  sx={({ palette: { grey, dark }, borders: { borderRadius } }) => ({
                    borderRadius: borderRadius.md,
                    cursor: "pointer",
                    transition: "all 300ms linear",

                    "&:hover": {
                      backgroundColor: grey[200],
                      color: dark.main,
                    },
                  })}
                >
                  {name}
            </MDTypography>
            )}

      </ListItem>)
        :
        (
        <><ListItem component="li" onClick={handleClick}>
        {!isTopbar ? (<MDBox
              {...rest}
              sx={(theme) => collapseItem(theme, {
                active,
                transparentSidenav,
                whiteSidenav,
                darkMode,
                sidenavColor
              })}
            >
              <ListItemIcon
                sx={(theme) => collapseIconBox(theme, { transparentSidenav, whiteSidenav, darkMode, active })}
              >
                {typeof icon === "string" ? (
                  <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
                ) : (
                  icon
                )}
              </ListItemIcon>
              <ListItemText
                primary={name}
                sx={(theme) => collapseText(theme, {
                  miniSidenav,
                  transparentSidenav,
                  whiteSidenav,
                  active
                })} />
                {open ? <ExpandLess /> : <ExpandMore />}
            </MDBox>)
          : (<><MDTypography
                  key={name}
                  minWidth="11.25rem"
                  display="block"
                  variant="button"
                  color="text"
                  textTransform="capitalize"
                  fontWeight="regular"
                  py={0.625}
                  pr={.1}
                  pl={2}
                  sx={({ palette: { grey, dark }, borders: { borderRadius } }) => ({
                    borderRadius: borderRadius.md,
                    cursor: "pointer",
                    transition: "all 300ms linear",

                    "&:hover": {
                      backgroundColor: grey[200],
                      color: dark.main,
                    },
                  })}
                >
                  {name} {open ? <ExpandLess /> : <ExpandMore />}
              </MDTypography>
            </>)}
          </ListItem>
          <Collapse in={open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {items.map(item => {
                  const activeSubLink = collapseName === item.key;
                  return (
                    !isTopbar ?(<NavLink key={item.name} to={item.route} className="sub-link">
                    <ListItem component="li"  sx={{ pl: 2 }}>
                      <MDBox
                        {...rest}
                        sx={(theme) => collapseItem(theme, {
                          active : activeSubLink,
                          transparentSidenav,
                          whiteSidenav,
                          darkMode,
                          sidenavColor
                        })}
                        >
                        <ListItemIcon
                          sx={(theme) => collapseIconBox(theme, { transparentSidenav, whiteSidenav, darkMode, active : activeSubLink })}
                        >
                          {typeof item.icon === "string" ? (
                            <Icon sx={(theme) => collapseIcon(theme, { active : activeSubLink })}>{item.icon}</Icon>
                          ) : (
                            item.icon
                          )}
                        </ListItemIcon>

                        <ListItemText
                          primary={item.name}
                          sx={(theme) => collapseText(theme, {
                            miniSidenav,
                            transparentSidenav,
                            whiteSidenav,
                            active :activeSubLink
                          })} />
                      </MDBox>
                    </ListItem>
                  </NavLink>
                  ):(
                    <MDTypography
                      key={item.name}
                      component={item.route ? Link : MuiLink}
                      to={item.route ? item.route : ""}
                      href={item.href ? item.href : (e) => e.preventDefault()}
                      target={item.href ? "_self" : ""}
                      rel={item.href ? "noreferrer" : "noreferrer"}
                      minWidth="11.25rem"
                      display="block"
                      variant="button"
                      color="text"
                      textTransform="capitalize"
                      fontWeight="regular"
                      py={0.625}
                      px={2}
                      sx={({ palette: { grey, dark }, borders: { borderRadius } }) => ({
                        borderRadius: borderRadius.md,
                        cursor: "pointer",
                        transition: "all 300ms linear",

                        "&:hover": {
                          backgroundColor: grey[200],
                          color: dark.main,
                        },
                      })}
                    >
                      {item.name}
                  </MDTypography>)
                )})}
              </List>
            </Collapse></>
          )
      }
    </>
  );
}

// Setting default values for the props of SidenavCollapse
SidenavCollapse.defaultProps = {
  active: false,
};

// Typechecking props for the SidenavCollapse
SidenavCollapse.propTypes = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
};

export default SidenavCollapse;
